import type { GridMode } from "@lancer-vtt/shared";
import { DEFAULT_GRID_MODE, err, isGridMode, ok, type Result } from "@lancer-vtt/shared";
import type { Affordance } from "./model/affordance";
import type { ActivationState, Cell, MoveReactionDecline, SplitMove, AreaEffect, AreaEffectId, Annotation, AttackLock, Combatant, EncounterEnvironment, EncounterState, FlightHeading, HeldAction, Room, RulingSpec, Side, SitrepConfig, SitrepProgress, StatusExpiry, StatusInstance, Turn, EventEnd } from "./types";
import { ACTIONS_PER_TURN, freshActivation, isEncounterEnvironment, isEncounterGround } from "./types";
import { emptyEdges, emptyTerrain, terrainMapProblem, type EdgeMap, type TerrainMap } from "./terrain";

/**
 * The schema version this build writes for encounters. Mirrors the roster's
 * {@link ../migrate CURRENT_SCHEMA_VERSION}. Bump on every persisted shape change, whether or
 * not it needs a transform, and append the matching {@link ENCOUNTER_SCHEMA_FOLDS} entry.
 *
 * **Versions 2–17 had real folds, and they are gone.** Each one is recorded in a single line in
 * {@link ENCOUNTER_SCHEMA_FOLDS} below; the transforms themselves were deleted by
 * `encounter-migrator-unwired` Phase 3 once the v18 wall made every one of them unreachable, and
 * git holds them. Do not restore one: a blob old enough to need it is refused at load (next note).
 *
 * **v18 — the wall.** `affordance-table` Phase 3.1 deleted the five string-keyed maps that
 * described the frozen action economy and its play-time state — `base.actionIds`,
 * `base.actionUsageCaps`, `base.actionRecharge`, `current.discharged`, and the `scene_use:` /
 * `limited:` prefixes of `current.resources` — replacing all five with `base.affordances`
 * (one {@link import("./model/affordance").Affordance} record per action) and
 * `current.affordanceState` (one entry per affordance or weapon instance), with NO fold
 * (`decisions/0176`, "No migration path").
 *
 * A pre-v18 blob carries the old maps and no record table, and `migrateCombatants` spreads each
 * combatant, so migrating one would produce a unit that spawns fine, renders an empty action bar
 * and silently grants every capped ability unlimited uses — corruption no diff and no error would
 * surface. So {@link loadEncounter} refuses any blob below
 * {@link MIN_LOADABLE_ENCOUNTER_SCHEMA_VERSION} instead. Developer ruling, 2026-09-24: add the
 * wall; old encounters are re-created on the current build, never hand-edited or migrated.
 * **Do not add a fold here.** A compatibility shim, an upgrade transform, or a "read the old shape
 * if present" branch would each re-create the parallel-map reading that refactor deleted.
 *
 * **v19 — a PURE TAG ADVANCE, no fold (the v9/v10/v17 posture, `decisions/0179`).**
 * `unit-control-assignment` Phase G added `Combatant.visibilityGrantSlotIds?: string[]` — the
 * visibility grant, beside the existing `coControllerSlotIds` control grant. It is additive and
 * optional, `migrateCombatants` spreads each stored combatant verbatim, and an absent list is
 * the honest "nobody has been granted a view of this unit" reading — which is also exactly
 * today's behaviour for every unit that predates the field. So a pre-v19 blob is already valid
 * v19 shape and there is **nothing to fold**. The bump exists for the same reason v17's did:
 * the ladder below is what makes a cross-branch version collision fail loudly, so every shape
 * change appends an entry whether or not it transforms anything. **Do not add a fold here.**
 *
 * **v20 — the second wall (`decisions/0185`).** `terrain-tile-types` changed what a terrain cell
 * stores: a cell now holds a tile reference (`{ tile: "<catalogue id>" }`, or
 * `{ tile: "custom", properties }`), and the engine resolves the cell's mechanics through the
 * catalogue. Before v20 a cell stored its flags directly (`{ blocksMovement: true, cover: "hard" }`).
 * A flag-shaped cell has no `tile`, so it resolves to no properties: loaded as-is, every wall,
 * cover cell and hazard on the map would silently become open ground. Developer ruling,
 * 2026-09-25: old maps are not supported. There is no fold and no `adopt-tiles` command; the one
 * live map (prod `landing-zone`) is converted by hand, with `scripts/landing-zone-tile-swap.ts` (deleted after the 2026-09-28 deploy; in git history at code `0d71b197`).
 * So {@link MIN_LOADABLE_ENCOUNTER_SCHEMA_VERSION} rises to 20 and {@link loadEncounter} refuses
 * any older blob. It also checks every stored cell of a v20 blob, so a hand-edited file carrying a
 * flag-shaped cell or an unknown tile id is refused with the cell named. **Do not add a fold
 * here** either: a flag-to-tile converter is exactly the stamping design `decisions/0185` rejected.
 *
 * **v21 — a PURE TAG ADVANCE, no fold (the v19 posture).** `terrain-tile-art` 6.1 added
 * `EncounterState.ground?: EncounterGround`, the map ground the terrain art paints (ruling 8). It is
 * additive and optional, `migrate` carries a known value (like `environment`) and drops anything
 * else, and an absent field reads as the default (forest floor), which is what every map painted
 * before it. **Do not add a fold here.**
 *
 * **v22 — a PURE TAG ADVANCE, no fold (the v19 posture).** `between-encounter-flow` 3.2 added
 * `Combatant.persistentNpcKey?: string`, the keyed allied NPC's commit write target
 * (`decisions/0196`). It is additive and optional, `migrateCombatants` spreads it through, and an
 * absent key is an ordinary ephemeral NPC, which is every NPC spawned before it. **Do not add a
 * fold here.**
 *
 * **v23 — a PURE TAG ADVANCE, no fold (the v19 posture).** `landing-zone-full-automation` 5.1 added
 * `activation.splitMove?: SplitMove`, a movement held partway for a Covering Fire shot. It is
 * additive and optional, {@link migrateActivation} carries a well-formed record and drops anything
 * else, and an absent record means no move is held, which is every encounter saved before it.
 * **Do not add a fold here.**
 *
 * **v24 — a real fold (`decisions/0202`).** `npc-frequency-caps` Phase 2 split an affordance's one
 * `cap` into a rate (`cap: {grain, max}`) and a Limited charge pool (`limited: {max, pool?}`). A v23
 * record carried a Limited pool as `cap: {grain: "scene", max, limited: true, pool?}`.
 * {@link migrateAffordances} rewrites that into `limited: {max, pool}` and drops the cap, so the
 * pool keeps its ledger slot (`pool ?? key`) and its persistence. Nothing else moves: a v23 record
 * could hold only one fact, so no folded record carries both. Pilot gear's old unmarked
 * `{grain: "scene", max}` cap stays a scene rate on `key` — the same slot and the same gate, and the
 * commit extractor never read pilot gear — so it reads identically without being rewritten.
 *
 * **v25 — a PURE TAG ADVANCE, no fold (the v19 posture).** `end-combat` added
 * `EncounterState.combatEndedAt?: string`, the GM's `end_combat` stamp. It is additive and
 * optional, `migrate` carries a string and drops anything else, and an absent stamp is a live
 * fight, which is every encounter saved before it. **Do not add a fold here.**
 *
 * **v26 — a PURE TAG ADVANCE, no fold (the v19 posture).** `npc-gaps` 1.5 added
 * `base.structureTable?: FrozenStructureTable`, the pack table a Monstrosity rolls for structure
 * damage. It is additive and optional, `migrateCombatants` spreads the base through, and an absent
 * table is the core mech table, which is what every NPC spawned before it rolled. **Do not add a
 * fold here.**
 *
 * **v27 — a PURE TAG ADVANCE, no fold (the v19 posture).** `npc-gaps` 2.4 added `base.npcTag?:
 * string`, the Vehicle or Ship template's forced NPC tag. It is additive and optional, and an absent
 * tag is an untagged NPC, which is every NPC spawned before it. **Do not add a fold here.**
 *
 * **v28 — a PURE TAG ADVANCE, no fold (the v19 posture).** `event-engine-lifecycle` 4.1 added
 * `StatusInstance.endsOn?: EventEnd[]`, the events that end a status early (Active Camouflage's
 * "until you take damage"). It is additive and optional, {@link migrateStatuses} carries the known
 * kinds and drops anything else, and an absent list means only the expiry ends the status, which
 * is every status saved before it. **Do not add a fold here.**
 *
 * **The forward log across the wall.** `encounters/<id>.log` never carries terrain: every
 * `set_terrain` / `apply_terrain_map` is logged as a `manual` breadcrumb with its command `type`
 * and no payload, and replay skips manual entries other than base edits (`history.ts`,
 * `appendLogEntries`). So an old log holds nothing in the flag format, and a converted
 * encounter keeps its log unchanged. `tiles.test.ts` pins that a terrain command's log entry
 * carries no terrain, so this stays true.
 */
export const CURRENT_ENCOUNTER_SCHEMA_VERSION = 28;

/**
 * The v18 wall (`decisions/0176`). Below {@link MIN_LOADABLE_ENCOUNTER_SCHEMA_VERSION} every blob
 * is refused; {@link loadEncounter} only uses this to say which wall a blob is behind, because a
 * pre-v18 blob's first problem is its action economy, not its terrain.
 */
const V18_ECONOMY_WALL = 18;

/**
 * The oldest stored encounter `schemaVersion` {@link loadEncounter} accepts — the v20 wall
 * (`decisions/0185`), which replaced the v18 wall (`decisions/0176`, amended 2026-09-24). Raising
 * it is how a no-fold shape change refuses old data; lowering it would need a fold the walls exist
 * to rule out.
 */
export const MIN_LOADABLE_ENCOUNTER_SCHEMA_VERSION = 20;

/**
 * The declared fold ladder — one entry per schema version this file has ever advanced to.
 *
 * **This exists to make a cross-branch schema-version collision fail loudly, and that is the
 * only reason it exists. Do not delete it as redundant with the prose above.**
 *
 * The failure it catches: two branches that each bump {@link CURRENT_ENCOUNTER_SCHEMA_VERSION}
 * by one, off the same base, write the *same* one-line edit. Git merges identical edits with no
 * conflict, so the result is a single version number silently gating two unrelated folds — data
 * corruption that no test sees. (This happened: see the v15 note above.) With this registry,
 * each branch also *appends an entry*, and the merge has only two possible outcomes:
 * - the appends collide textually → a real conflict marker, which is loud; or
 * - they merge cleanly into two entries carrying the **same `version`** → the uniqueness
 *   assertion in `migrate.test.ts` goes red, which is also loud.
 *
 * `max(version) === CURRENT_ENCOUNTER_SCHEMA_VERSION` is the other half: it forces the append.
 * A bump with no matching entry — **including a pure tag advance that writes no transform** —
 * reds the test, which is why the tag-advance versions below are listed too rather than skipped.
 *
 * Entries 2–17 are history only: their transforms were deleted behind the v18 wall (see
 * {@link CURRENT_ENCOUNTER_SCHEMA_VERSION}), but the entries stay because the pin reads the whole
 * ladder and because they are the one-line record of what each version changed.
 *
 * Gaps are legal, though there are none right now — 14 was vacant while the
 * `action-ordering-audit` lane held it and filled in when that lane merged. Order is the source
 * of truth for the ascending assertion, so append, never insert.
 */
export const ENCOUNTER_SCHEMA_FOLDS: readonly { version: number; fold: string }[] = [
  { version: 2, fold: "`Combatant.statuses`: bare `string[]` lifted to `StatusInstance[]` (`migrateStatuses`)" },
  { version: 3, fold: "top-level `round`/`status`/`turnBudget` folded into `ActivationState` (`migrateActivation`)" },
  { version: 4, fold: "flat `reactions` ledger split into `reactionSlot` + fresh `reactionUses`" },
  { version: 5, fold: "additive `annotations` map; absent/garbage folds to `{}`" },
  { version: 6, fold: "additive `activation.heldActions`; folds to a fresh `{}`" },
  { version: 7, fold: "additive `activation.activationsUsed`; folds to a fresh `{}`" },
  { version: 8, fold: "additive `activation.actionRoundUses`; folds to a fresh `{}`" },
  { version: 9, fold: "additive optional `Combatant.mineProfile` — tag advance only, no transform" },
  { version: 10, fold: "stat clusters null-sentinel → optional, plus `base.destructionOutcome`/`allegiance`/`coreActionIds` — tag advance only, no transform" },
  { version: 11, fold: "additive `environment` tag; unknown/absent folds to `undefined`" },
  { version: 12, fold: "additive `areaEffects` map; absent/garbage folds to `{}`" },
  { version: 13, fold: "`StatusExpiry` string → matcher object, plus `activation.turnOrdinal` — no preserving transform; unrecognized `expiry` coerces to `until_removed`" },
  { version: 14, fold: "additive `TurnBudget.spent`, SYNTHESIZED from the old remaining-budget proxy rather than defaulted (`coerceTurn`) — the `action-ordering-audit` lane's fold, merged 2026-08-28" },
  { version: 15, fold: "`base.saveTarget` re-homed onto the printed/computed value for `mech`/`npc` (`migrateSaveTarget`) — version-gated, kind-scoped" },
  { version: 16, fold: "`base.save` renamed/split: the roll bonus moves to `base.saveBonus` per kind and `save` is dropped (`migrateSaveBonus`) — version-gated, kind-scoped" },
  { version: 17, fold: "`StatusExpiry`'s `save_ends` arm gains an additive optional `stat` (the causing save's HASE axis) — tag advance only, no per-combatant transform; `coerceStatusExpiry` widened to carry it through instead of dropping it" },
  { version: 18, fold: "the five string-keyed action-economy maps (`base.actionIds`/`actionUsageCaps`/`actionRecharge`, `current.discharged`, and `current.resources`' `scene_use:`/`limited:` prefixes) deleted in favour of `base.affordances` + `current.affordanceState` — NO FOLD, deliberately (`decisions/0176`): the bump exists so a pre-v18 world fails loudly rather than loading with an empty economy" },
  { version: 19, fold: "additive optional `Combatant.visibilityGrantSlotIds` (the visibility grant, `decisions/0179`) — a PURE TAG ADVANCE, no fold: `migrateCombatants` spreads it through and an absent list already reads as 'nobody granted a view'" },
  { version: 20, fold: "terrain cells store a tile reference (`{ tile }` / `{ tile: \"custom\", properties }`) instead of flags (`decisions/0185`) — NO FOLD, deliberately: the second wall; a pre-v20 blob is refused so a flag-format map fails loudly rather than loading as open ground" },
  { version: 21, fold: "additive optional `EncounterState.ground` (the map ground, `terrain-tile-art` ruling 8) — a PURE TAG ADVANCE, no fold: an absent ground reads as forest floor" },
  { version: 22, fold: "additive optional `Combatant.persistentNpcKey` (a keyed allied NPC's commit write target, `decisions/0196`) — a PURE TAG ADVANCE, no fold: an absent key is an ordinary ephemeral NPC" },
  { version: 23, fold: "additive optional `activation.splitMove` (a movement held partway for a Covering Fire shot, `decisions/0195` point 3) — a PURE TAG ADVANCE, no fold: an absent record means no move is held" },
  { version: 24, fold: "an affordance's Limited pool moves out of `cap` into its own `limited` field (`decisions/0202`): a `cap` carrying `limited: true` becomes `limited: {max, pool}` and the cap is dropped (`migrateAffordances`)" },
  { version: 25, fold: "additive optional `EncounterState.combatEndedAt` (the GM's `end_combat` stamp) — a PURE TAG ADVANCE, no fold: an absent stamp is a live fight" },
  { version: 26, fold: "additive optional `base.structureTable` (a Monstrosity's pack structure damage table, `npc-gaps` 1.5) — a PURE TAG ADVANCE, no fold: an absent table is the core mech table" },
  { version: 27, fold: "additive optional `base.npcTag` (the Vehicle or Ship template's forced NPC tag, `npc-gaps` 2.4) — a PURE TAG ADVANCE, no fold: an absent tag is an untagged NPC" },
  { version: 28, fold: "additive optional `StatusInstance.endsOn` (events that end a status early, `event-engine-lifecycle` D8) — a PURE TAG ADVANCE, no fold: an absent list means only its expiry ends it" },
];

/**
 * Coerce a persisted value to a valid {@link StatusExpiry}; anything unrecognized defaults to
 * `{ kind: "until_removed" }`.
 *
 * **This runs on EVERY load** — `migrateStatuses` is unconditional, so `save_ends`'s optional
 * `stat` (`decisions/0163` D4) has to be
 * carried through HERE or a perfectly current instance loses its axis on the very next
 * world-restart/reload round-trip, silently regressing every re-save on it to the D5 `+0`
 * fallback. A non-string `stat` (or its absence) coerces to `undefined`, same as any other
 * garbage-defaults-safely field in this function.
 */
function coerceStatusExpiry(raw: unknown): StatusExpiry {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { kind: "until_removed" };
  const e = raw as { kind?: unknown; edge?: unknown; who?: unknown; stat?: unknown; status?: unknown };
  switch (e.kind) {
    case "save_ends":
      return typeof e.stat === "string" ? { kind: "save_ends", stat: e.stat } : { kind: "save_ends" };
    case "end_of_round":
    case "until_removed":
    case "scene":
    case "movement":
      return { kind: e.kind };
    case "while_status":
      return typeof e.status === "string" ? { kind: "while_status", status: e.status } : { kind: "until_removed" };
    case "turn": {
      const edge = e.edge === "start" || e.edge === "end" ? e.edge : "end";
      const who = e.who === "target" || e.who === "source" || e.who === "partner" ? e.who : "target";
      return { kind: "turn", edge, who };
    }
    default:
      return { kind: "until_removed" };
  }
}

/**
 * Coerce a persisted {@link RulingSpec} (the GM-minted ad-hoc ruling a `grant_ruling` snapshots
 * onto a status instance); an absent or off-shape value → `undefined`. `reason` is the one
 * required field, so a value without one is not a ruling. The optional mechanical fields are
 * carried by reference under the file's deliberately-shallow posture — re-validating
 * `extraDamage`'s nested dice here would be a second definition of {@link RulingSpec}.
 */
function coerceRulingSpec(raw: unknown): RulingSpec | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  return typeof (raw as { reason?: unknown }).reason === "string" ? (raw as RulingSpec) : undefined;
}

/**
 * Validate one combatant's `statuses` as a {@link StatusInstance}[]. Total over any input: each
 * named entry is kept (an unknown `expiry` coerced to `until_removed` via
 * {@link coerceStatusExpiry}); a nameless or non-object entry is dropped; a non-array → no
 * statuses.
 *
 * **This literal is the THIRD reconstruction site in this file, and it is a loss surface for
 * exactly the same reason as the other two** (`decisions/0169`). `migrateCombatants` spreads
 * the combatant and so is total over `Combatant`'s own optionals, but it replaces `statuses`
 * wholesale with this function's output — so every optional field added to
 * {@link StatusInstance} has to be enumerated HERE or it is dropped on the first migrating
 * load. Five were (`partner`, `weapon`, `cause`, `suspended`, `ruling`), found while building
 * Phase 1's pin; the `Required<StatusInstance>` fixture in `migrate.test.ts` is what keeps the
 * sixth from joining them.
 */
function migrateStatuses(raw: unknown): StatusInstance[] {
  if (!Array.isArray(raw)) return [];
  const out: StatusInstance[] = [];
  for (const entry of raw) {
    if (entry && typeof entry === "object" && typeof (entry as { name?: unknown }).name === "string") {
      const e = entry as {
        name: string;
        expiry?: unknown;
        source?: unknown;
        partner?: unknown;
        weapon?: unknown;
        cause?: unknown;
        suspended?: unknown;
        appliedTurn?: unknown;
        ruling?: unknown;
        endsOn?: unknown;
      };
      const expiry = coerceStatusExpiry(e.expiry);
      const ruling = coerceRulingSpec(e.ruling);
      out.push({
        name: e.name,
        expiry,
        // `appliedTurn` (v13): the duration bus reads it for a `turn`-kind expiry, and COMP/CON
        // Engaged's take-back reads it on an `until_removed` one. A stored non-number is dropped.
        ...(typeof e.appliedTurn === "number" ? { appliedTurn: e.appliedTurn } : {}),
        ...(typeof e.source === "string" ? { source: e.source } : {}),
        // The paired-relationship pointer (a mutual Grapple bind). Dropping it orphans the
        // break gesture on both markers.
        ...(typeof e.partner === "string" ? { partner: e.partner } : {}),
        // The weapon-instance key that pins a follow-up reservation. It is the STRUCTURAL
        // discriminator of a reservation, so dropping it turns a reservation into an
        // unmatched status and the "same weapon" constraint silently stops being checkable.
        ...(typeof e.weapon === "string" ? { weapon: e.weapon } : {}),
        // The closed `"tech"` provenance tag Shut Down reads to clear tech-caused statuses by
        // origin rather than by name.
        ...(e.cause === "tech" ? { cause: "tech" as const } : {}),
        // The expiry-timer suspend flag. Dropping it un-freezes a deliberately-held timer.
        ...(typeof e.suspended === "boolean" ? { suspended: e.suspended } : {}),
        // The GM's ad-hoc ruling. When present it REPLACES the pack-overlay lookup, so a
        // dropped ruling makes a ruling-carrying status mechanically inert.
        ...(ruling !== undefined ? { ruling } : {}),
        // The events that end it early (Active Camouflage's damage). Dropping them keeps it on past its RAW end.
        ...(coerceEventEnds(e.endsOn) ?? {}),
      });
    }
    // Anything else (a nameless or non-object entry) is dropped.
  }
  return out;
}

/** A persisted `endsOn`, keeping only the known event kinds; `undefined` when none survive. */
function coerceEventEnds(raw: unknown): { endsOn: EventEnd[] } | undefined {
  if (!Array.isArray(raw)) return undefined;
  const endsOn = raw.flatMap((e): EventEnd[] => (e && typeof e === "object" && ((e as { on?: unknown }).on === "damage" || (e as { on?: unknown }).on === "move") ? [{ on: (e as { on: "damage" | "move" }).on }] : []));
  return endsOn.length > 0 ? { endsOn } : undefined;
}

/**
 * The v24 fold (`decisions/0202`): a v23 `cap` carrying `limited: true` is a Limited charge pool,
 * rewritten into `limited: {max, pool}` with the cap dropped. Any other record passes through by
 * reference, so the fold is idempotent and a v24 table is returned unchanged.
 */
function migrateAffordances(raw: Affordance[] | undefined): Affordance[] | undefined {
  if (!Array.isArray(raw) || !raw.some((a) => (a.cap as { limited?: unknown } | undefined)?.limited === true)) return raw;
  return raw.map((a) => {
    const old = a.cap as { max: number; limited?: unknown; pool?: string } | undefined;
    if (old?.limited !== true) return a;
    const { cap: _cap, ...rest } = a;
    return { ...rest, limited: { max: old.max, ...(old.pool !== undefined ? { pool: old.pool } : {}) } };
  });
}

/**
 * Validate a combatants record: each object-shaped combatant is SPREAD (so every `Combatant`
 * field, optional or not, survives) with its `statuses` re-validated by {@link migrateStatuses}
 * and its frozen affordance tables folded by {@link migrateAffordances}; a non-object entry is
 * dropped.
 */
function migrateCombatants(raw: unknown): Record<string, Combatant> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, Combatant> = {};
  for (const [id, c] of Object.entries(raw as Record<string, unknown>)) {
    if (!c || typeof c !== "object") continue;
    // `everRevealed` was the born-Hidden omission bit, retired by `decisions/0188`; drop it from older saves.
    const { everRevealed: _everRevealed, ...combatant } = c as Combatant & { statuses?: unknown; everRevealed?: unknown };
    out[id] = { ...combatant, base: migrateBaseAffordances(combatant.base), statuses: migrateStatuses(combatant.statuses) };
  }
  return out;
}

/** {@link migrateAffordances} over a base's own table and its frozen pilot block's; the base by reference when neither changes. */
function migrateBaseAffordances(base: Combatant["base"]): Combatant["base"] {
  if (!base || typeof base !== "object") return base;
  const affordances = migrateAffordances(base.affordances);
  const blockAffordances = migrateAffordances(base.pilotBlock?.affordances);
  if (affordances === base.affordances && blockAffordances === base.pilotBlock?.affordances) return base;
  return {
    ...base,
    ...(affordances !== undefined ? { affordances } : {}),
    ...(base.pilotBlock !== undefined && blockAffordances !== undefined ? { pilotBlock: { ...base.pilotBlock, affordances: blockAffordances } } : {}),
  };
}

/** Default map bound for a fresh encounter — a hex-disk of this radius around `(0,0)`. */
export const DEFAULT_MAP_RADIUS = 8;

/** A fresh, empty encounter at the current schema version. */
export function emptyEncounter(id: string, name: string, gridMode: GridMode = DEFAULT_GRID_MODE): EncounterState {
  return {
    schemaVersion: CURRENT_ENCOUNTER_SCHEMA_VERSION,
    id,
    name,
    gridMode,
    combatants: {},
    order: [],
    mapRadius: DEFAULT_MAP_RADIUS,
    terrain: emptyTerrain(),
    edges: emptyEdges(),
    rooms: {},
    annotations: {},
    areaEffects: {},
    activation: freshActivation(),
  };
}

/** Coerce a persisted flat reaction ledger (`Record<id, number>`) to a clean map; garbage → empty. */
function coerceReactionSlot(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, number> = {};
  for (const [id, n] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof n === "number") out[id] = n;
  }
  return out;
}

/** Coerce a persisted per-combatant named-reaction-use ledger (`Record<id, Record<name, number>>`); garbage → empty. */
function coerceReactionUses(raw: unknown): Record<string, Record<string, number>> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, Record<string, number>> = {};
  for (const [id, uses] of Object.entries(raw as Record<string, unknown>)) {
    if (!uses || typeof uses !== "object" || Array.isArray(uses)) continue;
    const inner: Record<string, number> = {};
    for (const [name, n] of Object.entries(uses as Record<string, unknown>)) {
      if (typeof n === "number") inner[name] = n;
    }
    out[id] = inner;
  }
  return out;
}

/**
 * Coerce a persisted per-combatant round-scoped action-use ledger
 * (`Record<id, Record<actionId, number>>`, v8); garbage → empty. Same shape/validation as
 * {@link coerceReactionUses}, kept separate per the file's one-coercer-per-field convention.
 */
function coerceActionRoundUses(raw: unknown): Record<string, Record<string, number>> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, Record<string, number>> = {};
  for (const [id, uses] of Object.entries(raw as Record<string, unknown>)) {
    if (!uses || typeof uses !== "object" || Array.isArray(uses)) continue;
    const inner: Record<string, number> = {};
    for (const [name, n] of Object.entries(uses as Record<string, unknown>)) {
      if (typeof n === "number") inner[name] = n;
    }
    out[id] = inner;
  }
  return out;
}

/**
 * Coerce a persisted `heldActions` map (v6) to a clean `Record<CombatantId, HeldAction>`;
 * garbage → empty. No prior blob ever carried this field (v5 and earlier predate the feature
 * entirely), so this is purely defensive validation of a v6+ blob, not a real-data fold.
 */
function coerceHeldActions(raw: unknown): Record<string, HeldAction> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, HeldAction> = {};
  for (const [id, held] of Object.entries(raw as Record<string, unknown>)) {
    if (!held || typeof held !== "object" || Array.isArray(held)) continue;
    const h = held as Record<string, unknown>;
    if (typeof h.readiedActionId !== "string" || typeof h.triggerText !== "string") continue;
    out[id] = {
      readiedActionId: h.readiedActionId,
      triggerText: h.triggerText,
      ...(typeof h.weapon === "string" ? { weapon: h.weapon } : {}),
      ...(typeof h.mount === "string" ? { mount: h.mount } : {}),
    };
  }
  return out;
}

/**
 * Coerce a persisted {@link Turn} (the `idle`/`acting` union); anything unrecognized → `idle`.
 *
 * **The `acting` arm is REBUILT here, not spread, so this literal is a loss surface in its own
 * right** — see {@link migrate}'s header and `decisions/0169`. Every field of the arm and of
 * {@link TurnBudget} must appear below; the `Required<…>` fixtures in `migrate.test.ts` are
 * what keep that true as fields are added.
 */
function coerceTurn(raw: unknown): Turn {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { phase: "idle" };
  const t = raw as { phase?: unknown; actor?: unknown; budget?: unknown; attack?: unknown; heading?: unknown };
  if (t.phase === "acting" && typeof t.actor === "string") {
    const b = t.budget && typeof t.budget === "object" && !Array.isArray(t.budget) ? (t.budget as Record<string, unknown>) : {};
    const actions = typeof b.actions === "number" ? b.actions : ACTIONS_PER_TURN;
    const move = typeof b.move === "number" ? b.move : 0;
    const s = b.spent && typeof b.spent === "object" && !Array.isArray(b.spent) ? (b.spent as Record<string, unknown>) : undefined;
    const spent = s !== undefined && typeof s.actions === "number" && typeof s.move === "number" ? { actions: s.actions, move: s.move } : undefined;
    // `spent` and the two per-turn use ledgers are all OPTIONAL on `TurnBudget`, so absence is
    // carried as absence rather than manufactured — their readers already treat a missing one as
    // "nothing spent / nothing used", and inventing a value would make a re-migrate of the
    // output differ from the input.
    const actionUses = coerceUseLedger(b.actionUses);
    const protocolUses = coerceUseLedger(b.protocolUses);
    const attack = coerceAttackLock(t.attack);
    const heading = coerceFlightHeading(t.heading);
    return {
      phase: "acting",
      actor: t.actor,
      budget: {
        actions,
        move,
        ...(spent !== undefined ? { spent } : {}),
        ...(actionUses !== undefined ? { actionUses } : {}),
        ...(protocolUses !== undefined ? { protocolUses } : {}),
      },
      ...(attack !== undefined ? { attack } : {}),
      ...(heading !== undefined ? { heading } : {}),
    };
  }
  return { phase: "idle" };
}

/**
 * Coerce one persisted per-turn use ledger ({@link TurnBudget.actionUses} /
 * {@link TurnBudget.protocolUses}) — a FLAT `Record<actionId, number>`, the same shape as
 * `reactionSlot`, **not** the two-level per-combatant shape {@link coerceActionRoundUses}
 * handles. Returns `undefined` for an absent or non-object field, because the field itself is
 * optional and "absent" is a meaningful state the migrator must not invent a value for.
 */
function coerceUseLedger(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  return coerceReactionSlot(raw);
}

/** The three RAW actions that may open an {@link AttackLock} — the `action` tag's closed set. */
const ATTACK_LOCK_ACTIONS = new Set(["skirmish", "barrage", "full_tech"]);

/**
 * Coerce a persisted mid-attack lock (the `acting` arm's optional {@link AttackLock}); an
 * absent or off-shape value → `undefined` (no attack in progress), never a fabricated lock.
 * A lock that survives a reload is what keeps a half-fired Barrage from becoming a free second
 * attack, so dropping a well-formed one is a real rules regression, not cosmetic.
 */
function coerceAttackLock(raw: unknown): AttackLock | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const a = raw as { action?: unknown; weapons?: unknown; ordnanceWindow?: unknown; mounts?: unknown };
  if (typeof a.action !== "string" || !ATTACK_LOCK_ACTIONS.has(a.action)) return undefined;
  if (!Array.isArray(a.weapons)) return undefined;
  return {
    action: a.action as AttackLock["action"],
    weapons: a.weapons.filter((w): w is string => typeof w === "string"),
    ...(typeof a.ordnanceWindow === "boolean" ? { ordnanceWindow: a.ordnanceWindow } : {}),
    ...(Array.isArray(a.mounts) ? { mounts: a.mounts.filter((m): m is string => typeof m === "string") } : {}),
  };
}

/**
 * Coerce a persisted Flight direction lock (the `acting` arm's optional
 * {@link FlightHeading}); an absent or off-shape value → `undefined`. The two endpoints are
 * carried by reference under the same deliberately-shallow posture `rooms`/`annotations` take
 * — a `Cell` is a tagged union and re-validating it here would be a second definition of it.
 */
function coerceFlightHeading(raw: unknown): FlightHeading | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const h = raw as { origin?: unknown; dest?: unknown };
  if (!h.origin || typeof h.origin !== "object") return undefined;
  if (!h.dest || typeof h.dest !== "object") return undefined;
  return h as FlightHeading;
}

/**
 * Coerce a persisted `activation.moveReactionDeclines` list: keep each entry whose three ids are
 * strings, and return `undefined` (the field stays absent) when nothing survives.
 */
function coerceMoveReactionDeclines(raw: unknown): MoveReactionDecline[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const kept = raw.filter(
    (d): d is MoveReactionDecline =>
      d !== null && typeof d === "object" && typeof d.watcherId === "string" && typeof d.moverId === "string" && typeof d.consumerId === "string",
  ).map((d) => ({ watcherId: d.watcherId, moverId: d.moverId, consumerId: d.consumerId }));
  return kept.length > 0 ? kept : undefined;
}

/**
 * Coerce a persisted `activation.splitMove`: carried when its ids are strings, its path a non-empty
 * list of cells and `at` an index into it, else `undefined`. Cells are carried by reference, the
 * shallow posture {@link coerceFlightHeading} takes.
 */
function coerceSplitMove(raw: unknown): SplitMove | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const m = raw as { moverId?: unknown; shooterId?: unknown; path?: unknown; at?: unknown; trampled?: unknown };
  if (typeof m.moverId !== "string" || typeof m.shooterId !== "string") return undefined;
  if (!Array.isArray(m.path) || m.path.length === 0 || !m.path.every((c) => c !== null && typeof c === "object")) return undefined;
  if (typeof m.at !== "number" || !Number.isInteger(m.at) || m.at < 0 || m.at >= m.path.length) return undefined;
  const trampled = Array.isArray(m.trampled) ? m.trampled.filter((id): id is string => typeof id === "string") : undefined;
  return { moverId: m.moverId, shooterId: m.shooterId, path: m.path as Cell[], at: m.at, ...(trampled !== undefined ? { trampled } : {}) };
}

/**
 * Coerce a persisted value to a valid {@link ActivationState}; every field is
 * validate-or-default, so garbage (or an absent activation) comes back fresh.
 */
function migrateActivation(raw: unknown): ActivationState {
  const a = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const activeSide: Side = a.activeSide === "npc" ? "npc" : "pc";
  const activated = Array.isArray(a.activated)
    ? a.activated.filter((x): x is string => typeof x === "string")
    : [];
  // Carried through when present and well-formed, dropped otherwise — absent means none.
  const moveReactionDeclines = coerceMoveReactionDeclines(a.moveReactionDeclines);
  const splitMove = coerceSplitMove(a.splitMove);
  return {
    round: typeof a.round === "number" ? a.round : 1,
    activeSide,
    activated,
    reactionSlot: coerceReactionSlot(a.reactionSlot),
    reactionUses: coerceReactionUses(a.reactionUses),
    turn: coerceTurn(a.turn),
    heldActions: coerceHeldActions(a.heldActions),
    // Same flat `Record<CombatantId, number>` shape as `reactionSlot`, so its coercer doubles.
    activationsUsed: coerceReactionSlot(a.activationsUsed),
    actionRoundUses: coerceActionRoundUses(a.actionRoundUses),
    turnOrdinal: typeof a.turnOrdinal === "number" ? a.turnOrdinal : 0,
    ...(moveReactionDeclines !== undefined ? { moveReactionDeclines } : {}),
    ...(splitMove !== undefined ? { splitMove } : {}),
  };
}

/**
 * Validate persisted encounter data at or above the v18 wall and stamp it with
 * {@link CURRENT_ENCOUNTER_SCHEMA_VERSION}. Every step is validate-or-default per field and
 * idempotent, so a current-version blob round-trips unchanged; garbage falls back to an empty
 * encounter.
 *
 * **This does NOT enforce the wall** — {@link loadEncounter} does, and it is the only export
 * production reaches. Called on a pre-v18 blob this would happily stamp it current with its
 * deleted economy maps riding through; that is exactly what the wall refuses.
 *
 * **The loss surface of this file is the RECONSTRUCTION, not any fold** (`decisions/0169`).
 * This function does not mutate its input — it rebuilds `EncounterState` from the
 * hand-enumerated object literal below, and {@link coerceTurn} and {@link migrateStatuses}
 * rebuild the `acting` turn and each `StatusInstance` the same way. A field that exists on the
 * type but is missing from one of those three literals is silently dropped, and because every
 * field added to these types since they were written has been OPTIONAL, TypeScript reports
 * nothing. Fourteen live fields had accumulated that way by the time it was measured: five
 * durable ones here (`gridWidth`, `gridHeight`, `showEnemyHp`, `sitrep`, `sitrepProgress`),
 * four mid-turn ones in `coerceTurn` (`actionUses`, `protocolUses`, `attack`, `heading`), and
 * five per-status ones in `migrateStatuses` (`partner`, `weapon`, `cause`, `suspended`,
 * `ruling`). All fourteen are carried now.
 *
 * **What keeps it that way is the pin, not this comment.** `migrate.test.ts` holds a `FULL`
 * fixture typed `Required<EncounterState>` — with `Required<TurnBudget>`,
 * `Required<Extract<Turn, { phase: "acting" }>>`, `Required<ActivationState>` and
 * `Required<StatusInstance>` sub-fixtures — every optional set to a distinguishable
 * non-default, asserted to round-trip through `migrate()` identically. Adding an optional field
 * to any of those types fails to COMPILE until the fixture populates it, and then fails the
 * round-trip assertion until the matching literal here carries it. `migrateCombatants`,
 * `migrateActivation` and the `coerce*` helpers each construct their output the same
 * enumerated way and are covered by the same fixture; the roster migrator
 * (`../migrate.ts`) is the counter-example that needs no pin, because it spreads instead of
 * enumerating and so cannot exhibit the defect at all.
 *
 */
export function migrate(data: unknown, fallbackId: string, fallbackName: string): EncounterState {
  if (data === null || typeof data !== "object") {
    return emptyEncounter(fallbackId, fallbackName);
  }

  const state = data as Partial<EncounterState>;
  const combatants = migrateCombatants(state.combatants);

  return {
    schemaVersion: CURRENT_ENCOUNTER_SCHEMA_VERSION,
    id: typeof state.id === "string" ? state.id : fallbackId,
    name: typeof state.name === "string" ? state.name : fallbackName,
    // Additive grid mode — a stored, known mode is carried; a pre-grid-mode (or garbage) blob
    // defaults to 2d-square (the v1 mode).
    gridMode:
      typeof state.gridMode === "string" && isGridMode(state.gridMode)
        ? (state.gridMode as GridMode)
        : DEFAULT_GRID_MODE,
    combatants,
    order: Array.isArray(state.order) ? (state.order as EncounterState["order"]) : [],
    mapRadius:
      typeof state.mapRadius === "number" && state.mapRadius >= 0
        ? state.mapRadius
        : DEFAULT_MAP_RADIUS,
    terrain:
      state.terrain && typeof state.terrain === "object" && !Array.isArray(state.terrain)
        ? (state.terrain as TerrainMap)
        : emptyTerrain(),
    edges:
      state.edges && typeof state.edges === "object" && !Array.isArray(state.edges)
        ? (state.edges as EdgeMap)
        : emptyEdges(),
    // v11: additive/optional environment tag — a stored, known
    // value is carried; a blob predating environments, one that never set it, or a stray
    // unrecognized string (garbage) all default to `undefined` ("no special environment").
    environment:
      typeof state.environment === "string" && isEncounterEnvironment(state.environment)
        ? (state.environment as EncounterEnvironment)
        : undefined,
    // v21: the map ground, carried like `environment`; absent or unknown reads as the default.
    ground: typeof state.ground === "string" && isEncounterGround(state.ground) ? state.ground : undefined,
    // Additive purely-visual rooms layer — a stored map is carried; a blob predating rooms
    // (or garbage) defaults to empty (no rooms).
    rooms:
      state.rooms && typeof state.rooms === "object" && !Array.isArray(state.rooms)
        ? (state.rooms as Record<string, Room>)
        : {},
    // v5: additive purely-visual, player-writable annotations layer — a stored map is carried;
    // a blob predating annotations (or garbage) defaults to empty. Same posture as rooms above.
    annotations:
      state.annotations && typeof state.annotations === "object" && !Array.isArray(state.annotations)
        ? (state.annotations as Record<string, Annotation>)
        : {},
    // v12: additive standing area-effects layer — a stored map is carried; a blob predating
    // area effects (or garbage) defaults to empty (no areas).
    // Same posture as rooms/annotations above; deliberately shallow validation (object-shaped,
    // not Array), matching the file's existing convention rather than a per-field schema check.
    areaEffects:
      state.areaEffects && typeof state.areaEffects === "object" && !Array.isArray(state.areaEffects)
        ? (state.areaEffects as Record<AreaEffectId, AreaEffect>)
        : {},
    // The square grid's own extent, written by `resize_grid`. Additive/optional, so the
    // posture is carry-a-valid-number-or-`undefined` (NOT `mapRadius`'s default-to-a-constant
    // — an absent extent is meaningfully different from a stored one, and `gridExtent` is the
    // reader that turns absence into the inscribed-square fallback). The numeric check mirrors
    // `mapRadius`'s and is deliberately NOT the reducer's `[MIN_GRID_DIM, MAX_GRID_DIM]` range:
    // a stricter check here would DROP a stored out-of-range dimension, which is the exact
    // class of loss this literal is being repaired for.
    gridWidth:
      typeof state.gridWidth === "number" && state.gridWidth >= 0 ? state.gridWidth : undefined,
    gridHeight:
      typeof state.gridHeight === "number" && state.gridHeight >= 0 ? state.gridHeight : undefined,
    // The per-encounter live-HP policy — additive/optional boolean; a non-boolean (or absent)
    // value folds to `undefined`, which `encounterShowEnemyHp` reads as the RAW-faithful
    // `false`.
    showEnemyHp: typeof state.showEnemyHp === "boolean" ? state.showEnemyHp : undefined,
    // The authored sitrep and Control's per-round score. Deliberately SHALLOW validation
    // (object-shaped, not Array), matching the `rooms`/`annotations`/`areaEffects` convention
    // above rather than inventing a per-field schema — a deep check here would be a second
    // definition of `SitrepConfig` to keep in sync, and getting it wrong drops authored
    // mission objectives that are derivable from nothing else on the blob.
    sitrep:
      state.sitrep && typeof state.sitrep === "object" && !Array.isArray(state.sitrep)
        ? (state.sitrep as SitrepConfig)
        : undefined,
    sitrepProgress:
      state.sitrepProgress &&
      typeof state.sitrepProgress === "object" &&
      !Array.isArray(state.sitrepProgress)
        ? (state.sitrepProgress as SitrepProgress)
        : undefined,
    // v25: the fight's end stamp, carried as a string; absent or garbage reads as a live fight.
    combatEndedAt: typeof state.combatEndedAt === "string" ? state.combatEndedAt : undefined,
    activation: migrateActivation(state.activation),
  };
}

/**
 * Load a persisted encounter blob: refuse it if it sits outside
 * [{@link MIN_LOADABLE_ENCOUNTER_SCHEMA_VERSION}, {@link CURRENT_ENCOUNTER_SCHEMA_VERSION}],
 * otherwise {@link migrate} it. The one door every production read of persisted encounter
 * bytes goes through.
 *
 * - **Below 18 (or no `schemaVersion` at all)** — the v18 wall (`decisions/0176`, amended
 *   2026-09-24). The blob carries the deleted action-economy maps and no affordance records;
 *   there is no fold, by ruling, so the only fix is to re-create the encounter on this build.
 * - **18 or 19** — the v20 wall (`decisions/0185`). The blob stores terrain as flag-format
 *   cells, which would load as open ground; there is no fold, by ruling, so the fix is to
 *   re-create the encounter or convert its map by hand (`scripts/landing-zone-tile-swap.ts` at code `0d71b197`).
 * - **A v20 blob with a bad terrain cell** — a flag-shaped cell or an unknown tile id, named.
 * - **Above current** — written by a newer build (a rolled-back deploy). `migrate` rebuilds
 *   the state from an enumerated literal, so a field this build has never heard of would be
 *   dropped on the first command's write. Refusing keeps the file intact for the newer build.
 * - **Not an object** — nothing to validate; refusing beats booting an empty room whose first
 *   write would overwrite the file.
 *
 * The error names the problem and the fix but not the file: the caller knows where the bytes
 * came from and prefixes that.
 */
export function loadEncounter(data: unknown, id: string): Result<EncounterState> {
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    return err("the stored state is not an encounter object");
  }
  const stored = (data as { schemaVersion?: unknown }).schemaVersion;
  if (typeof stored !== "number" || stored < V18_ECONOMY_WALL) {
    return err(
      `it is encounter schema ${typeof stored === "number" ? `v${stored}` : "with no schemaVersion"}, older than the ` +
        `v${V18_ECONOMY_WALL} wall. v${V18_ECONOMY_WALL} replaced the action-economy maps with affordance records and has no migration by design ` +
        "(decisions/0176): loading it would bring every unit up with an empty action bar. Move the file (and its .log) " +
        "out of the world folder, restart, and re-create the encounter on this build",
    );
  }
  if (stored < MIN_LOADABLE_ENCOUNTER_SCHEMA_VERSION) {
    return err(
      `it is encounter schema v${stored}, older than the v${MIN_LOADABLE_ENCOUNTER_SCHEMA_VERSION} wall. ` +
        `v${MIN_LOADABLE_ENCOUNTER_SCHEMA_VERSION} stores terrain as named tiles, and this file stores flag-format ` +
        "terrain, which has no migration by design (decisions/0185): loading it would turn every wall, cover cell and " +
        "hazard into open ground. Move the file (and its .log) out of the world folder, restart, and either re-create " +
        "the encounter on this build or convert its map to tiles by hand (scripts/landing-zone-tile-swap.ts at code 0d71b197 shows how)",
    );
  }
  if (stored > CURRENT_ENCOUNTER_SCHEMA_VERSION) {
    return err(
      `it is encounter schema v${stored}, newer than this build's v${CURRENT_ENCOUNTER_SCHEMA_VERSION} — it was written ` +
        "by a later build, and loading it here would drop the fields this build does not know on the first write. " +
        "Deploy the newer build again",
    );
  }
  const terrain = (data as { terrain?: unknown }).terrain;
  const terrainProblem = terrain === undefined ? null : terrainMapProblem(terrain);
  if (terrainProblem !== null) {
    return err(
      `its ${terrainProblem}. v${CURRENT_ENCOUNTER_SCHEMA_VERSION} terrain cells are tile references ` +
        '(`{ "tile": "<id>" }` or `{ "tile": "custom", "properties": {…} }`, decisions/0185). Fix the cell by hand, or ' +
        "move the file (and its .log) out of the world folder and re-create the encounter",
    );
  }
  return ok(migrate(data, id, id));
}
